import { COMMENDA_IO_EMAIL_DOMAIN } from '@commenda/utils';

import {
  IndirectTaxFilingPipeline,
  IndirectTaxRegion,
  IndirectTaxRegionHelper,
} from '../../common';

/**
 * Identifies a single action in the filing detail kebab menu. Which keys a
 * filing exposes is decided per IndirectTaxFilingPipeline (see
 * PIPELINE_ACTION_KEYS), mirroring the stages the filings dashboard status
 * modal drives.
 */
export enum IndirectTaxFilingActionKey {
  GENERATE_BREAKDOWNS = 'GENERATE_BREAKDOWNS',
  VIEW_GENERATED_REPORTS = 'VIEW_GENERATED_REPORTS',
  EXPORT_BREAKDOWN = 'EXPORT_BREAKDOWN',
  EXPORT_BREAKDOWN_WITH_LINE_ITEMS = 'EXPORT_BREAKDOWN_WITH_LINE_ITEMS',
  GENERATE_REPORT = 'GENERATE_REPORT',
  EXPORT_REPORT = 'EXPORT_REPORT',
  EXPORT_JURISDICTION_TAX_REPORT = 'EXPORT_JURISDICTION_TAX_REPORT',
}

/**
 * Pure decisions behind the filing detail actions menu: which actions a
 * region exposes, their display labels, which require an email recipient,
 * and whether the viewer is a Commenda internal user. No React, so the
 * rules stay testable in plain TS.
 */
export class IndirectTaxFilingActionsHelper {
  private static readonly JURISDICTION_TAX_REPORT_SUPPORTED_JURISDICTION_IDS: ReadonlySet<string> =
    new Set([
      'STATE_CEN_05', // Arkansas
      'STATE_CEN_06', // California
      'STATE_CEN_13', // Georgia
      'STATE_CEN_20', // Kansas
      'STATE_CEN_27', // Minnesota
      'STATE_CEN_31', // Nebraska
      'STATE_CEN_36', // New York
      'STATE_CEN_37', // North Carolina
      'STATE_CEN_38', // North Dakota
      'STATE_CEN_39', // Ohio
      'STATE_CEN_40', // Oklahoma
      'STATE_CEN_46', // South Dakota
      'STATE_CEN_49', // Utah
      'STATE_CEN_54', // West Virginia
    ]);

  /**
   * Menu contents per pipeline, in display order. Two-stage pipelines list
   * the breakdown step first, then the report (contribution) step, then the
   * supporting breakdown exports; the US-only breakdown-report viewer is
   * omitted there because the contribution report is the filed artefact.
   */
  private static readonly PIPELINE_ACTION_KEYS: Readonly<
    Record<IndirectTaxFilingPipeline, ReadonlyArray<IndirectTaxFilingActionKey>>
  > = {
    [IndirectTaxFilingPipeline.BREAKDOWN]: [
      IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS,
      IndirectTaxFilingActionKey.VIEW_GENERATED_REPORTS,
      IndirectTaxFilingActionKey.EXPORT_BREAKDOWN,
      IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS,
    ],
    [IndirectTaxFilingPipeline.CONTRIBUTION]: [
      IndirectTaxFilingActionKey.GENERATE_REPORT,
      IndirectTaxFilingActionKey.EXPORT_REPORT,
    ],
    [IndirectTaxFilingPipeline.BREAKDOWN_THEN_CONTRIBUTION]: [
      IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS,
      IndirectTaxFilingActionKey.GENERATE_REPORT,
      IndirectTaxFilingActionKey.EXPORT_REPORT,
      IndirectTaxFilingActionKey.EXPORT_BREAKDOWN,
      IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS,
    ],
  };

  private static readonly ACTION_LABELS: Readonly<
    Record<IndirectTaxFilingActionKey, string>
  > = {
    [IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS]:
      'Generate breakdowns for filing',
    [IndirectTaxFilingActionKey.VIEW_GENERATED_REPORTS]:
      'View generated tax breakdown reports',
    [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN]:
      'Export tax breakdown report',
    [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS]:
      'Export tax breakdowns report with line items',
    [IndirectTaxFilingActionKey.GENERATE_REPORT]: 'Generate report',
    [IndirectTaxFilingActionKey.EXPORT_REPORT]: 'Export report',
    [IndirectTaxFilingActionKey.EXPORT_JURISDICTION_TAX_REPORT]:
      'Export state configured report',
  };

  /** Actions that email a generated file and therefore need a recipient. */
  private static readonly EMAIL_DELIVERY_ACTION_KEYS: ReadonlySet<IndirectTaxFilingActionKey> =
    new Set([
      IndirectTaxFilingActionKey.EXPORT_BREAKDOWN,
      IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS,
      IndirectTaxFilingActionKey.EXPORT_REPORT,
      IndirectTaxFilingActionKey.EXPORT_JURISDICTION_TAX_REPORT,
    ]);

  /**
   * Generate actions mutate filing state the detail page renders, so the
   * caller should refresh after they succeed. Exports email a file and
   * leave the filing untouched.
   */
  private static readonly FILING_REFRESHING_ACTION_KEYS: ReadonlySet<IndirectTaxFilingActionKey> =
    new Set([
      IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS,
      IndirectTaxFilingActionKey.GENERATE_REPORT,
    ]);

  /** Success-toast copy per mutation action (sentence case). */
  private static readonly ACTION_SUCCESS_MESSAGES: Readonly<
    Record<IndirectTaxFilingActionKey, string>
  > = {
    [IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS]:
      'Tax breakdown generation in progress',
    [IndirectTaxFilingActionKey.VIEW_GENERATED_REPORTS]: '',
    [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN]:
      'Tax breakdown report export in progress',
    [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS]:
      'Tax breakdown report export in progress',
    [IndirectTaxFilingActionKey.GENERATE_REPORT]:
      'Report generation in progress',
    [IndirectTaxFilingActionKey.EXPORT_REPORT]: 'Report export in progress',
    [IndirectTaxFilingActionKey.EXPORT_JURISDICTION_TAX_REPORT]:
      'State tax report export in progress',
  };

  /**
   * Action keys for a filing's region, in display order. Returns an empty
   * array for regions that don't classify (e.g. ALL_REGIONS) so the menu
   * can hide itself rather than render an empty dropdown.
   */
  static getActionKeysForRegion(
    region: IndirectTaxRegion
  ): IndirectTaxFilingActionKey[] {
    const pipeline = IndirectTaxRegionHelper.getFilingPipeline(region);
    if (!pipeline) return [];
    return [...IndirectTaxFilingActionsHelper.PIPELINE_ACTION_KEYS[pipeline]];
  }

  static getActionLabel(key: IndirectTaxFilingActionKey): string {
    return IndirectTaxFilingActionsHelper.ACTION_LABELS[key];
  }

  static actionRequiresEmail(key: IndirectTaxFilingActionKey): boolean {
    return IndirectTaxFilingActionsHelper.EMAIL_DELIVERY_ACTION_KEYS.has(key);
  }

  static actionRefreshesFiling(key: IndirectTaxFilingActionKey): boolean {
    return IndirectTaxFilingActionsHelper.FILING_REFRESHING_ACTION_KEYS.has(
      key
    );
  }

  static getActionSuccessMessage(key: IndirectTaxFilingActionKey): string {
    return IndirectTaxFilingActionsHelper.ACTION_SUCCESS_MESSAGES[key];
  }

  static isJurisdictionTaxReportSupported(jurisdictionId: string): boolean {
    return IndirectTaxFilingActionsHelper.JURISDICTION_TAX_REPORT_SUPPORTED_JURISDICTION_IDS.has(
      jurisdictionId
    );
  }

  /** True when the email belongs to a Commenda staff member. */
  static isInternalUser(email?: string): boolean {
    return !!email?.toLowerCase().includes(COMMENDA_IO_EMAIL_DOMAIN);
  }
}
